import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, OnDestroy, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, DataMap, GridPanel } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FeatureType,
    Grid,
    IModalEditOptions,
    ListItem,
    ModalBackDrop,
    ModalEditMode,
    ModalSize,
    PurchaseOrder,
    PurchaseOrderTypeEx,
    VpJob,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { PurchaseOrderComponent } from './purchase-order.component'

@Component({
    selector: 'app-purchase-orders',
    templateUrl: './purchase-orders.component.html',
    styleUrls: ['./purchase-orders.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
    ],
})
export class PurchaseOrdersComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch
    private destroyRef = inject(DestroyRef)
    private router = inject(Router)
    grid: Grid<PurchaseOrder>
    vendorMap!: DataMap
    phaseMap!: DataMap
    allUserMap!: DataMap
    activeUserMap!: DataMap
    vendors: any[] = []
    approvedVendors: any[] = []

    selectedJob = signal<string>('')
    jobMap: ListItem<string>[] = []
    activeJobs: VpJob[] = []

    viewingRecords = signal(false)

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<PurchaseOrder>(this.services)
            .setupModalEditing(
                this.services.data.purchaseOrders,
                'Purchase Order',
                PurchaseOrderComponent,
                () => new PurchaseOrder(),
                { component: this, isInitialWizard: false },
                ModalSize.large,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [this.getTypeDisplay(item.type), item.vendorName, item.phase, item.description, item.poNumber])

        // The image URLs come from the server, so when we add or edit items, we need to refetch to get the URLs.
        this.grid.modified.on(this.refreshItems.bind(this))

        this.refresh()

        if (!this.router.url.includes('/list')) {
            this.openWizardModal()
        }
    }

    private activeWizardModal: PurchaseOrderComponent | null = null

    private openWizardModal() {
        const target = new PurchaseOrder()
        const modalEditOptions: IModalEditOptions<PurchaseOrder> = {
            target: target,
            initializeParams: { component: this, isInitialWizard: true },
            mode: ModalEditMode.adding,
            repository: this.services.data.purchaseOrders,
            typeName: 'Purchase Order',
        }

        const ref = this.services.modals.open(PurchaseOrderComponent, ModalSize.extraLarge, ModalBackDrop.darkAndInactive)
        const repoModal = ref.componentInstance as PurchaseOrderComponent
        repoModal.initialize(modalEditOptions)
        this.activeWizardModal = repoModal

        ref.result.then(
            result => {
                this.activeWizardModal = null
                if (result && result.viewRecords) {
                    // User chose "View Records"
                    this.viewingRecords.set(true)
                } else {
                    // User saved a PO -- show the grid so they can see it
                    this.viewingRecords.set(true)
                    this.refreshItems()
                }
            },
            () => {
                this.activeWizardModal = null
                this.viewingRecords.set(true)
            },
        )
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
    }

    onJobChanged() {
        const job = this.selectedJob()
        if (job && job !== '') {
            this.refreshItems()
        } else {
            this.grid.items = []
        }
    }

    refreshItems() {
        const job = this.selectedJob()
        if (!job || job === '') {
            return
        }
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.purchaseOrders.queryAllByAction('GetByJob', new HttpParams().set('job', job)),
                this.services.data.get('api/PurchaseOrder/GetApprovedVendorsByJob', new HttpParams().set('job', job)),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result[0]
                    this.approvedVendors = result[1] as any[]
                    // Resolve job names from activeJobs (this page does not load vpJobs)
                    this.grid.items.forEach(po => {
                        const job = this.activeJobs.find(j => j.id === po.job)
                        if (job) po.jobName = job.name
                    })
                },
            })
        this.cancelableRequest('', true, sub)
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.vendors.queryAll(),
                this.services.data.get('api/PurchaseOrder/GetPhasesForPurchaseOrder'),
                this.services.data.get<VpJob[]>('api/JobPoConfiguration/GetJobsForAuthorizedUser'),
                this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.vendors = result[0]
                    this.vendorMap = new DataMap(this.vendors, 'id', 'name')

                    const phases = result[1] as any[]
                    const phaseListItems = phases.map(x => new ListItem(x.value, x.display))
                    this.phaseMap = new DataMap(phaseListItems, 'value', 'display')

                    // Jobs where the current user is an Authorized PO User (from Job PO Configuration)
                    this.activeJobs = result[2] as VpJob[]
                    this.buildJobMap()

                    // If the wizard modal is open and waiting for the job list, notify it
                    if (this.activeWizardModal && this.activeWizardModal.isJobListLoading) {
                        this.activeWizardModal.onJobListReady()
                    }

                    // We want the grid to show a value for any user, even inactive ones.
                    this.allUserMap = new DataMap(result[3] as VpUser[], 'id', 'name')

                    // When the grid is edited, we will only show active users as options.
                    this.activeUserMap = new DataMap(result[4] as VpUser[], 'id', 'name')

                    this.initialLoadingComplete()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    buildJobMap() {
        const jobs: ListItem<string>[] = this.activeJobs
            .map(job => new ListItem<string>(job.id, job.id + ' ' + job.name))
            .sort((a, b) => a.display.localeCompare(b.display))

        // Only add placeholder when there are real jobs; otherwise jobMap stays empty
        // so the wizard can show "No jobs are configured for you" instead of a useless combo.
        this.jobMap = jobs.length > 0 ? [new ListItem<string>('', 'Select a Job'), ...jobs] : []
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png?w=500'
        }
        return './Resources/Images/PurchaseOrder/' + url + '?w=500'
    }

    openImage(url) {
        window.open('./Resources/Images/PurchaseOrder/' + url, '_blank')
    }

    itemFormatter = (panel: GridPanel, _r, _c, _cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            // Add any custom formatting if needed
        }
    }

    getTypeDisplay(type: number): string {
        return PurchaseOrderTypeEx.toDisplay(type)
    }
}
